import { Outlet, useLocation, useNavigate } from 'react-router';
import { LogOut, Settings, User as UserIcon, Receipt } from 'lucide-react';
import {
  UniversalAppLayout,
  UniversalTopNav,
  UniversalSubNav,
  UniversalAppFooter,
  UserMenu,
  ThemeToggle,
  BrandLogo,
  brandColors,
} from '@sighthost/ui';
import { siteConfig } from '@sighthost/config';
import { useCurrentCustomer } from '@/domains/customers';
import { supabase } from '@/shared/lib/supabase';
import { clientPrimaryNav, clientSubNavByPrefix } from './ClientNavigation';
import { AnnouncementBanner } from '@/domains/announcements/components/AnnouncementBanner';

const APP_THEME_ACCENT = brandColors.primary;

/**
 * ClientLayoutWrapper — Outlet-based wrapper for every authenticated client route.
 *
 * Composes the universal app shell:
 *   - Logo + brand mark on the far left
 *   - Centred primary nav (Dashboard / Services / Domains / Billing / Support / Knowledgebase)
 *   - Profile-icon menu on the far right
 *   - Section sub-nav under the top bar (resolved from current pathname)
 *   - Top-bar accent strip in the brand orange
 *   - Single-line corporate footer (copyright + reg) at the very bottom
 */
export function ClientLayoutWrapper() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { data: customer } = useCurrentCustomer();

  const subNavConfig = clientSubNavByPrefix.find((s) =>
    pathname.startsWith(s.prefix)
  );

  const userName = customer?.fullName ?? 'Customer';
  const userEmail = customer?.email;

  async function signOut() {
    try {
      await supabase.auth.signOut();
    } catch {
      // Fall through to navigation regardless.
    }
    // Hard navigate out of the SPA to the marketing site, not React Router.
    // Customers expect to land on the marketing site on logout, not on the
    // portal sign-in page. siteConfig.url is the canonical apex.
    window.location.href = siteConfig.url;
  }

  return (
    <UniversalAppLayout
      topNav={
        <UniversalTopNav
          accentColor={APP_THEME_ACCENT}
          brand={
            <span className="flex items-center gap-3">
              <BrandLogo tone="white" className="h-10" loading="eager" />
              <span className="hidden font-mono text-[9px] uppercase tracking-[0.3em] text-white/50 sm:block">
                Customer Portal
              </span>
            </span>
          }
          items={clientPrimaryNav}
          right={
            <>
            {/* Same control as the admin and sales shells — one component, one
                store, one storage key. Hidden below `sm`: the mobile bar has no
                room, and `system` already tracks the phone own setting. */}
            <ThemeToggle onAccent className="mr-1 hidden sm:inline-flex" />
            <UserMenu
              name={userName}
              email={userEmail}
              items={[
                {
                  label: 'Profile settings',
                  icon: UserIcon,
                  onSelect: () => navigate('/app/settings/profile'),
                },
                {
                  label: 'Account & security',
                  icon: Settings,
                  onSelect: () => navigate('/app/settings/security'),
                },
                {
                  label: 'Billing info',
                  icon: Receipt,
                  onSelect: () => navigate('/app/settings/billing-info'),
                },
                {
                  label: 'Sign out',
                  icon: LogOut,
                  tone: 'danger',
                  separated: true,
                  onSelect: signOut,
                },
              ]}
            />
            </>
          }
        />
      }
      subNav={subNavConfig && <UniversalSubNav items={subNavConfig.items} />}
      footer={
        <UniversalAppFooter
          legalName={siteConfig.legalName}
          registration={siteConfig.registration}
        />
      }
    >
      <AnnouncementBanner audience="customers" />
      <Outlet />
    </UniversalAppLayout>
  );
}
